import { useState, useContext, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Chip,
  FormControl,
  InputLabel,
  OutlinedInput,
  Checkbox,
  ListItemText,
  IconButton,
  Tabs,
  Tab,
  Autocomplete,
  Grid,
  Paper,
  Tooltip,
  LinearProgress,
  Fade,
  Divider,
  Snackbar,
  Alert,
  alpha,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  IconX,
  IconBrandInstagram,
  IconBrandTiktok,
  IconBrandYoutube,
  IconBrandLinkedin,
  IconBrandTwitter,
  IconBrandFacebook,
  IconLink,
  IconUpload,
  IconPhoto,
  IconMoodSmile,
  IconTrash,
  IconCloudUpload,
  IconSparkles,
  IconCalendarEvent,
  IconWorld,
  IconTag,
  IconSend,
  IconPlayerPlay,
  IconClock,
  IconCheckbox,
  IconCopy,
  IconCheck,
  IconCalendar,
  IconRocket,
} from "@tabler/icons-react";
import EmojiPicker from "emoji-picker-react";
import dayjs from "dayjs";
import Button from "../components/Button";
import UserContext from "../contexts/UserContext";
import { fetchGet, fetchPost, handleUpload } from "../util/helpers";

const PLATFORMS = [
  { value: "facebook", label: "Facebook", icon: IconBrandFacebook, color: "#1877F2" },
  { value: "instagram", label: "Instagram", icon: IconBrandInstagram, color: "#C13584" },
  { value: "tiktok", label: "TikTok", icon: IconBrandTiktok, color: "#000" },
  { value: "youtube", label: "YouTube", icon: IconBrandYoutube, color: "#FF0000" },
  { value: "linkedin", label: "LinkedIn", icon: IconBrandLinkedin, color: "#0A66C2" },
  { value: "twitter", label: "X (Twitter)", icon: IconBrandTwitter, color: "#000" },
];

const CHAR_LIMITS = {
  facebook: 5000,
  instagram: 2200,
  tiktok: 2200,
  youtube: 5000,
  linkedin: 3000,
  twitter: 280,
};

const COUNTRIES = [
  "United States", "Canada", "United Kingdom", "Australia", "Germany",
  "France", "Brazil", "India", "Japan", "Mexico", "Spain", "Italy",
  "Netherlands", "South Korea", "Sweden", "Singapore", "Nigeria",
  "South Africa", "Argentina", "Colombia", "Global",
];

const sectionLabel = (icon, text) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
    <Box sx={{
      width: 28, height: 28, borderRadius: 1.5, display: "flex", alignItems: "center", justifyContent: "center",
      background: "linear-gradient(135deg, #EDE9FE, #F3E8FF)",
      color: "#7C3AED",
    }}>
      {icon}
    </Box>
    <Typography sx={{ fontWeight: 700, fontSize: 13, color: "#374151", letterSpacing: "-0.01em" }}>{text}</Typography>
  </Box>
);

export default function PostComposerDialog({ open, onClose, onPostCreated }) {
  const { user } = useContext(UserContext);
  const [platforms, setPlatforms] = useState([]);
  const [channels, setChannels] = useState([]);
  const [availableChannels, setAvailableChannels] = useState([]);
  const [caption, setCaption] = useState("");
  const [media, setMedia] = useState([]);
  const [tags, setTags] = useState([]);
  const [targetCountry, setTargetCountry] = useState(null);
  const [scheduledAt, setScheduledAt] = useState(null);
  const [shortenedLink, setShortenedLink] = useState("");
  const [affiliateLink, setAffiliateLink] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [mediaTab, setMediaTab] = useState(0);
  const [assetFolders, setAssetFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [folderFiles, setFolderFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const [userRole, setUserRole] = useState("admin");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [scheduleDateTime, setScheduleDateTime] = useState(null);
  const [shortenLoading, setShortenLoading] = useState(false);

  const canPublish = platforms.length > 0 && caption.trim().length > 0;
  const hasPublishPermission = userRole === "admin" || userRole === "publisher";

  useEffect(() => {
    if (open) {
      fetchGet("/team/channels").then((res) => {
        if (res?.result) setAvailableChannels(res.result);
      }).catch(() => {});
      fetchGet("/media/folders").then((res) => {
        if (res?.result) setAssetFolders(res.result);
      }).catch(() => {});
      fetchGet("/team/my-role").then((res) => {
        if (res?.result?.role) setUserRole(res.result.role);
      }).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    if (selectedFolder) {
      fetchGet(`/media/folders/${selectedFolder}/files`).then((res) => {
        if (res?.result) setFolderFiles(res.result);
      }).catch(() => {});
    }
  }, [selectedFolder]);

  const minCharLimit = platforms.length > 0
    ? Math.min(...platforms.map((p) => CHAR_LIMITS[p] || 5000))
    : 5000;

  const charPct = Math.min((caption.length / minCharLimit) * 100, 100);

  const normalizeLinkInput = (raw) => {
    if (raw == null || typeof raw !== "string") return "";
    let s = raw.trim();
    if (!s) return "";
    if (!/^https?:\/\//i.test(s)) s = `https://${s}`;
    try {
      const u = new URL(s);
      if (!["http:", "https:"].includes(u.protocol)) return "";
      return u.href;
    } catch {
      return "";
    }
  };

  const handleShortenLink = async () => {
    const normalized = normalizeLinkInput(affiliateLink);
    if (!normalized) {
      setSnackbar({
        open: true,
        message: "Enter a valid URL (e.g. example.com or https://example.com/path)",
        severity: "error",
      });
      return;
    }
    setAffiliateLink(normalized);
    setShortenLoading(true);
    try {
      const res = await fetchPost("/social/shorten-link", { url: normalized });
      if (res?.result?.shortUrl) {
        setShortenedLink(res.result.shortUrl);
        setSnackbar({ open: true, message: "Short link created. Copy it below to use in your post.", severity: "success" });
      } else {
        setSnackbar({ open: true, message: "Could not create short link. Try again.", severity: "error" });
      }
    } catch (e) {
      let msg = "Could not shorten link. Check you are signed in and try again.";
      try {
        if (e && typeof e.json === "function") {
          const body = await e.json();
          if (body?.message) msg = body.message;
        }
      } catch {
        /* ignore */
      }
      console.error("Error shortening link:", e);
      setSnackbar({ open: true, message: msg, severity: "error" });
    }
    setShortenLoading(false);
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shortenedLink);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const processFiles = async (files) => {
    if (!files.length) return;
    setUploading(true);
    try {
      const uploads = await Promise.all(
        files.map(async (file) => {
          const fileId = `${Date.now()}_${file.name}`;
          const url = await handleUpload(file, "social_media", fileId);
          return { url, fileName: file.name, fileType: file.type };
        })
      );
      setMedia((prev) => [...prev, ...uploads]);
    } catch (e) {
      console.error("Upload error:", e);
    }
    setUploading(false);
  };

  const handleFileUpload = (e) => processFiles(Array.from(e.target.files));

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    processFiles(Array.from(e.dataTransfer.files));
  };

  const handleSelectAsset = (file) => {
    if (!media.find((m) => m.url === file.url)) {
      setMedia((prev) => [...prev, { url: file.url, fileName: file.fileName, fileType: file.fileType }]);
    }
  };

  const handleRemoveMedia = (index) => {
    setMedia((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEmojiClick = (emojiData) => {
    setCaption((prev) => prev + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  const togglePlatform = (value) => {
    setPlatforms((prev) =>
      prev.includes(value) ? prev.filter((p) => p !== value) : [...prev, value]
    );
  };

  const buildPostBody = (status, extras = {}) => ({
    platforms,
    channels,
    caption,
    media,
    tags,
    targetCountry,
    scheduledAt: extras.scheduledAt || (scheduledAt ? scheduledAt.toISOString() : null),
    status,
    shortenedLink,
    ...extras,
  });

  const submitPost = async (status, extras = {}) => {
    setSubmitting(true);
    try {
      const body = buildPostBody(status, extras);
      const res = await fetchPost("/social/posts", body);
      if (res?.id && onPostCreated) onPostCreated(res.id);
      resetForm();
      onClose();
      return res;
    } catch (e) {
      console.error("Error creating post:", e);
    }
    setSubmitting(false);
  };

  const handleQueue = async () => {
    setSubmitting(true);
    try {
      const body = buildPostBody("queued");
      const res = await fetchPost("/social/posts", body);
      const scheduleNote = res?.scheduledAt
        ? ` ${dayjs(res.scheduledAt).format("MMM D, YYYY h:mm A")}`
        : "";
      setSnackbar({
        open: true,
        message: `Added to queue.${scheduleNote ? ` Scheduled for${scheduleNote}.` : ""}`,
        severity: "success",
      });
      if (res?.id && onPostCreated) onPostCreated(res.id, { skipNavigate: true });
      resetForm();
      // Close after a tick so the Snackbar mounts above the dialog and stays visible (no immediate navigate away).
      setTimeout(() => onClose(), 200);
    } catch (e) {
      console.error("Error queuing post:", e);
      let msg = "Failed to add to queue. Try again.";
      try {
        if (e && typeof e.json === "function") {
          const body = await e.json();
          if (body?.message) msg = body.message;
        }
      } catch {
        /* ignore */
      }
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleQueueClick = () => {
    if (!canPublish) {
      setSnackbar({
        open: true,
        message: "Select at least one platform and add a caption to add to the queue.",
        severity: "info",
      });
      return;
    }
    handleQueue();
  };

  const handleScheduleClick = () => {
    setScheduleDateTime(scheduledAt || dayjs().add(1, "hour"));
    setScheduleDialogOpen(true);
  };

  const handleScheduleConfirm = async () => {
    if (!scheduleDateTime) return;
    setScheduledAt(scheduleDateTime);
    setScheduleDialogOpen(false);
    await submitPost("queued", { scheduledAt: scheduleDateTime.toISOString() });
    setSnackbar({
      open: true,
      message: `Post scheduled for ${scheduleDateTime.format("MMM D, YYYY h:mm A")}`,
      severity: "success",
    });
  };

  const resetForm = () => {
    setPlatforms([]);
    setChannels([]);
    setCaption("");
    setMedia([]);
    setTags([]);
    setTargetCountry(null);
    setScheduledAt(null);
    setShortenedLink("");
    setAffiliateLink("");
    setShowEmojiPicker(false);
    setSelectedFolder(null);
    setLinkCopied(false);
    setDragOver(false);
    setScheduleDateTime(null);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        TransitionComponent={Fade}
        transitionDuration={300}
        PaperProps={{
          sx: {
            borderRadius: 4,
            maxHeight: "92vh",
            overflow: "hidden",
            boxShadow: "0 24px 80px rgba(0,0,0,0.18), 0 8px 24px rgba(124,58,237,0.08)",
          },
        }}
      >
        {/* Gradient Header */}
        <Box sx={{
          background: "linear-gradient(135deg, #7C3AED 0%, #A855F7 50%, #C084FC 100%)",
          px: { xs: 3, md: 4 },
          py: 3,
          position: "relative",
          overflow: "hidden",
        }}>
          <Box sx={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%", background: "rgba(255,255,255,0.08)" }} />
          <Box sx={{ position: "absolute", bottom: -20, left: "40%", width: 80, height: 80, borderRadius: "50%", background: "rgba(255,255,255,0.05)" }} />
          <Box sx={{ position: "relative", zIndex: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center",
                  bgcolor: "rgba(255,255,255,0.18)", backdropFilter: "blur(8px)",
                }}>
                  <IconSparkles size={20} color="#fff" />
                </Box>
                <Typography variant="h5" sx={{ color: "#fff", fontWeight: 800, letterSpacing: "-0.02em" }}>
                  Create a Post
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", fontSize: 13, pl: 6.5 }}>
                Craft, preview, and publish across your social channels
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              sx={{
                color: "#fff",
                bgcolor: "rgba(255,255,255,0.15)",
                backdropFilter: "blur(4px)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
                width: 36, height: 36,
              }}
            >
              <IconX size={18} />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, minHeight: 500 }}>
            {/* Left Column — Content */}
            <Box sx={{ flex: 1, p: { xs: 2.5, md: 3.5 }, borderRight: { md: "1px solid rgba(0,0,0,0.06)" }, overflow: "auto" }}>

              {/* Platform Buttons */}
              {sectionLabel(<IconRocket size={15} />, "Select Platforms")}
              <Box sx={{ display: "flex", gap: 1, mb: 3, flexWrap: "wrap" }}>
                {PLATFORMS.map((p) => {
                  const active = platforms.includes(p.value);
                  return (
                    <Box
                      key={p.value}
                      onClick={() => togglePlatform(p.value)}
                      sx={{
                        display: "flex", alignItems: "center", gap: 1,
                        px: 2, py: 1, borderRadius: 2.5,
                        cursor: "pointer",
                        border: active ? `2px solid ${p.color}` : "2px solid #E5E7EB",
                        bgcolor: active ? alpha(p.color, 0.06) : "#fff",
                        transition: "all 0.2s",
                        "&:hover": {
                          borderColor: p.color,
                          bgcolor: alpha(p.color, 0.04),
                          transform: "translateY(-1px)",
                          boxShadow: `0 4px 12px ${alpha(p.color, 0.15)}`,
                        },
                      }}
                    >
                      <p.icon size={18} color={active ? p.color : "#9CA3AF"} />
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: active ? p.color : "#6B7280" }}>
                        {p.label}
                      </Typography>
                      {active && (
                        <Box sx={{
                          width: 16, height: 16, borderRadius: "50%", bgcolor: p.color,
                          display: "flex", alignItems: "center", justifyContent: "center", ml: 0.25,
                        }}>
                          <IconCheck size={10} color="#fff" stroke={3} />
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Box>

              {/* Caption */}
              {sectionLabel(<IconSend size={15} />, "Caption")}
              <Box sx={{ position: "relative", mb: 3 }}>
                <TextField
                  fullWidth
                  multiline
                  minRows={5}
                  maxRows={10}
                  value={caption}
                  onChange={(e) => {
                    if (e.target.value.length <= minCharLimit) setCaption(e.target.value);
                  }}
                  placeholder="What's on your mind? Write an engaging caption..."
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      bgcolor: "#FAFBFC",
                      fontSize: 14,
                      lineHeight: 1.7,
                      "&.Mui-focused": {
                        bgcolor: "#fff",
                        boxShadow: "0 0 0 3px rgba(124, 58, 237, 0.1)",
                      },
                      "& fieldset": { borderColor: "rgba(0,0,0,0.08)" },
                      "&:hover fieldset": { borderColor: "rgba(124,58,237,0.3)" },
                      "&.Mui-focused fieldset": { borderColor: "#7C3AED" },
                    },
                  }}
                />
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1, px: 0.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Tooltip title="Add emoji" arrow>
                      <IconButton
                        size="small"
                        onClick={() => setShowEmojiPicker((p) => !p)}
                        sx={{
                          bgcolor: showEmojiPicker ? "rgba(124,58,237,0.1)" : "transparent",
                          color: showEmojiPicker ? "#7C3AED" : "#9CA3AF",
                          "&:hover": { bgcolor: "rgba(124,58,237,0.08)", color: "#7C3AED" },
                        }}
                      >
                        <IconMoodSmile size={18} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{ width: 60, height: 4, borderRadius: 2, bgcolor: "#E5E7EB", overflow: "hidden" }}>
                      <Box sx={{
                        width: `${charPct}%`, height: "100%", borderRadius: 2,
                        background: charPct > 90 ? "#EF4444" : charPct > 70 ? "#F59E0B" : "linear-gradient(90deg, #7C3AED, #A855F7)",
                        transition: "width 0.2s, background 0.3s",
                      }} />
                    </Box>
                    <Typography sx={{
                      fontSize: 11, fontWeight: 600, fontVariantNumeric: "tabular-nums",
                      color: charPct > 90 ? "#EF4444" : charPct > 70 ? "#F59E0B" : "#9CA3AF",
                    }}>
                      {caption.length}/{minCharLimit}
                    </Typography>
                  </Box>
                </Box>
                {showEmojiPicker && (
                  <Box sx={{
                    position: "absolute", zIndex: 10, bottom: 48, left: 0,
                    borderRadius: 3, overflow: "hidden",
                    boxShadow: "0 12px 40px rgba(0,0,0,0.15)",
                  }}>
                    <EmojiPicker onEmojiClick={handleEmojiClick} width={320} height={380} />
                  </Box>
                )}
              </Box>

              {/* Media Upload */}
              {sectionLabel(<IconPhoto size={15} />, "Media")}
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 3,
                  border: "1px solid rgba(0,0,0,0.06)",
                  overflow: "hidden",
                  mb: 3,
                }}
              >
                <Tabs
                  value={mediaTab}
                  onChange={(_, v) => setMediaTab(v)}
                  sx={{
                    px: 2, pt: 1, minHeight: 40,
                    "& .MuiTab-root": {
                      textTransform: "none", fontWeight: 600, fontSize: 12, minHeight: 36, py: 0.5,
                      color: "#9CA3AF",
                      "&.Mui-selected": { color: "#7C3AED" },
                    },
                    "& .MuiTabs-indicator": {
                      background: "linear-gradient(90deg, #7C3AED, #A855F7)", borderRadius: 2, height: 2.5,
                    },
                  }}
                >
                  <Tab label="Upload" icon={<IconUpload size={14} />} iconPosition="start" />
                  <Tab label="Asset Library" icon={<IconPhoto size={14} />} iconPosition="start" />
                </Tabs>

                <Box sx={{ p: 2 }}>
                  {mediaTab === 0 && (
                    <Box
                      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                      onDragLeave={() => setDragOver(false)}
                      onDrop={handleDrop}
                      onClick={() => fileInputRef.current?.click()}
                      sx={{
                        p: 3,
                        borderRadius: 3,
                        border: `2px dashed ${dragOver ? "#7C3AED" : "rgba(0,0,0,0.1)"}`,
                        bgcolor: dragOver ? "rgba(124,58,237,0.04)" : "#FAFBFC",
                        textAlign: "center",
                        cursor: "pointer",
                        transition: "all 0.2s",
                        "&:hover": {
                          borderColor: "#7C3AED",
                          bgcolor: "rgba(124,58,237,0.02)",
                        },
                      }}
                    >
                      <input ref={fileInputRef} type="file" hidden multiple accept="image/*,video/*" onChange={handleFileUpload} />
                      <Box sx={{
                        width: 48, height: 48, borderRadius: 3, mx: "auto", mb: 1.5,
                        background: "linear-gradient(135deg, #EDE9FE, #F3E8FF)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}>
                        <IconCloudUpload size={22} color="#7C3AED" />
                      </Box>
                      <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#374151", mb: 0.25 }}>
                        Drop files here or click to upload
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: "#9CA3AF" }}>
                        Supports images and videos
                      </Typography>
                    </Box>
                  )}

                  {mediaTab === 1 && (
                    <Box>
                      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Select Folder</InputLabel>
                        <Select
                          value={selectedFolder || ""}
                          onChange={(e) => setSelectedFolder(e.target.value)}
                          label="Select Folder"
                          sx={{ borderRadius: 2 }}
                        >
                          {assetFolders.map((f) => (
                            <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {folderFiles.length > 0 && (
                        <Grid container spacing={1}>
                          {folderFiles.map((file) => {
                            const selected = !!media.find((m) => m.url === file.url);
                            return (
                              <Grid item xs={4} sm={3} key={file.id}>
                                <Box
                                  sx={{
                                    position: "relative",
                                    cursor: "pointer",
                                    borderRadius: 2,
                                    overflow: "hidden",
                                    border: selected ? "2px solid #7C3AED" : "2px solid transparent",
                                    "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.1)" },
                                    transition: "all 0.2s",
                                  }}
                                  onClick={() => handleSelectAsset(file)}
                                >
                                  <Box
                                    component="img"
                                    src={file.url}
                                    alt={file.fileName}
                                    sx={{ width: "100%", height: 80, objectFit: "cover", display: "block" }}
                                  />
                                  {selected && (
                                    <Box sx={{
                                      position: "absolute", top: 4, right: 4,
                                      width: 20, height: 20, borderRadius: "50%",
                                      bgcolor: "#7C3AED", display: "flex", alignItems: "center", justifyContent: "center",
                                    }}>
                                      <IconCheck size={12} color="#fff" stroke={3} />
                                    </Box>
                                  )}
                                  <Typography noWrap sx={{ fontSize: 10, p: 0.5, color: "#6B7280" }}>{file.fileName}</Typography>
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      )}
                    </Box>
                  )}

                  {uploading && (
                    <LinearProgress sx={{
                      mt: 1.5, borderRadius: 2, height: 4,
                      bgcolor: "#EDE9FE",
                      "& .MuiLinearProgress-bar": { background: "linear-gradient(90deg, #7C3AED, #A855F7)" },
                    }} />
                  )}
                </Box>
              </Paper>

              {/* Media Preview Thumbnails */}
              {media.length > 0 && (
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 3 }}>
                  {media.map((m, i) => (
                    <Box key={i} sx={{
                      position: "relative",
                      width: 72, height: 72,
                      borderRadius: 2,
                      overflow: "hidden",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      "&:hover .media-overlay": { opacity: 1 },
                    }}>
                      <Box component="img" src={m.url} alt={m.fileName}
                        sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                      />
                      <Box className="media-overlay" sx={{
                        position: "absolute", inset: 0, bgcolor: "rgba(0,0,0,0.4)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        opacity: 0, transition: "opacity 0.2s",
                      }}>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveMedia(i)}
                          sx={{ bgcolor: "rgba(255,255,255,0.9)", width: 28, height: 28, "&:hover": { bgcolor: "#fff" } }}
                        >
                          <IconTrash size={14} color="#EF4444" />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Right Column — Settings */}
            <Box sx={{ width: { xs: "100%", md: 320 }, p: { xs: 2.5, md: 3 }, bgcolor: "#FAFBFC", overflow: "auto" }}>

              {/* Social Channels */}
              {sectionLabel(<IconPlayerPlay size={15} />, "Social Channels")}
              <FormControl fullWidth size="small" sx={{ mb: 3 }}>
                <InputLabel>Select Channels</InputLabel>
                <Select
                  multiple
                  value={channels}
                  onChange={(e) => setChannels(e.target.value)}
                  input={<OutlinedInput label="Select Channels" />}
                  sx={{
                    borderRadius: 2, bgcolor: "#fff",
                    "& fieldset": { borderColor: "rgba(0,0,0,0.08)" },
                  }}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((val) => (
                        <Chip key={val} label={val} size="small"
                          sx={{
                            fontWeight: 600, fontSize: 11, borderRadius: 1.5,
                            background: "linear-gradient(135deg, #F3F0FF, #EDE9FE)", color: "#7C3AED",
                          }}
                        />
                      ))}
                    </Box>
                  )}
                >
                  {availableChannels.map((ch) => (
                    <MenuItem key={ch.id} value={ch.name || ch.id}>
                      <Checkbox checked={channels.includes(ch.name || ch.id)} size="small" />
                      <ListItemText primary={ch.name || ch.platform} primaryTypographyProps={{ fontSize: 13 }} />
                    </MenuItem>
                  ))}
                  {availableChannels.length === 0 && (
                    <MenuItem disabled>
                      <ListItemText primary="No channels connected" primaryTypographyProps={{ fontSize: 13, color: "text.secondary" }} />
                    </MenuItem>
                  )}
                </Select>
              </FormControl>

              {/* Shortened Link */}
              {sectionLabel(<IconLink size={15} />, "Shortened Link")}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", gap: 0.75 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Paste URL..."
                    value={affiliateLink}
                    onChange={(e) => setAffiliateLink(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2, bgcolor: "#fff", fontSize: 13,
                        "& fieldset": { borderColor: "rgba(0,0,0,0.08)" },
                      },
                    }}
                  />
                  <Button
                    gradient
                    style={{ borderRadius: 10, padding: "6px 14px", fontSize: 12, minWidth: 70, fontWeight: 700 }}
                    onClick={handleShortenLink}
                    disabled={shortenLoading}
                  >
                    {shortenLoading ? "…" : "Shorten"}
                  </Button>
                </Box>
                {shortenedLink && (
                  <Box sx={{
                    mt: 1, p: 1, borderRadius: 2,
                    bgcolor: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.15)",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                  }}>
                    <Typography sx={{ fontSize: 12, color: "#059669", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {shortenedLink}
                    </Typography>
                    <Tooltip title={linkCopied ? "Copied!" : "Copy"} arrow>
                      <IconButton size="small" onClick={handleCopyLink} sx={{ color: "#059669" }}>
                        {linkCopied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>

              <Divider sx={{ mb: 2.5, borderColor: "rgba(0,0,0,0.06)" }} />

              {/* Targeting */}
              {sectionLabel(<IconWorld size={15} />, "Targeting")}
              <Autocomplete
                options={COUNTRIES}
                value={targetCountry}
                onChange={(_, val) => setTargetCountry(val)}
                size="small"
                sx={{ mb: 3 }}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select a country..."
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2, bgcolor: "#fff", fontSize: 13,
                        "& fieldset": { borderColor: "rgba(0,0,0,0.08)" },
                      },
                    }}
                  />
                )}
              />

              {/* Tags */}
              {sectionLabel(<IconTag size={15} />, "Meta Tags")}
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={tags}
                onChange={(_, val) => setTags(val)}
                size="small"
                sx={{ mb: 3 }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      size="small"
                      {...getTagProps({ index })}
                      key={option}
                      sx={{
                        fontWeight: 600, fontSize: 11, borderRadius: 1.5,
                        background: "linear-gradient(135deg, #F3F0FF, #EDE9FE)", color: "#7C3AED",
                        "& .MuiChip-deleteIcon": { color: "#A78BFA", "&:hover": { color: "#7C3AED" } },
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder="Add tags..."
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2, bgcolor: "#fff", fontSize: 13,
                        "& fieldset": { borderColor: "rgba(0,0,0,0.08)" },
                      },
                    }}
                  />
                )}
              />

              {/* Schedule */}
              {sectionLabel(<IconCalendar size={15} />, "Schedule")}
              <Box sx={{ mb: 3 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    value={scheduledAt}
                    onChange={(val) => setScheduledAt(val)}
                    minDateTime={dayjs()}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        size: "small",
                        placeholder: "Pick date & time...",
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2, bgcolor: "#fff", fontSize: 13,
                            "& fieldset": { borderColor: "rgba(0,0,0,0.08)" },
                            "&.Mui-focused fieldset": { borderColor: "#7C3AED" },
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
                {scheduledAt && (
                  <Paper elevation={0} sx={{
                    mt: 1, p: 1.5, borderRadius: 2,
                    bgcolor: "rgba(99,102,241,0.06)", border: "1px solid rgba(99,102,241,0.15)",
                  }}>
                    <Typography sx={{ fontSize: 12, color: "#4338CA", fontWeight: 600 }}>
                      Goes live: {dayjs(scheduledAt).format("ddd, MMM D, YYYY h:mm A")}
                    </Typography>
                  </Paper>
                )}
              </Box>

              {/* Permission hint */}
              {!hasPublishPermission && (
                <Paper elevation={0} sx={{
                  p: 1.5, borderRadius: 2, mb: 2,
                  bgcolor: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.15)",
                }}>
                  <Typography sx={{ fontSize: 11, color: "#92400E", fontWeight: 500, lineHeight: 1.5 }}>
                    Your role ({userRole}) requires approval before publishing. Use "Submit for Approval" to send to an admin.
                  </Typography>
                </Paper>
              )}
            </Box>
          </Box>
        </DialogContent>

        {/* Action Bar */}
        <Box sx={{
          px: { xs: 2, md: 4 }, py: 2,
          borderTop: "1px solid rgba(0,0,0,0.06)",
          bgcolor: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {platforms.length > 0 && (
              <Box sx={{ display: "flex", gap: 0.5, mr: 1 }}>
                {platforms.map((pVal) => {
                  const p = PLATFORMS.find((pl) => pl.value === pVal);
                  return p ? (
                    <Box key={pVal} sx={{
                      width: 26, height: 26, borderRadius: 1.5,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      bgcolor: alpha(p.color, 0.08),
                    }}>
                      <p.icon size={14} color={p.color} />
                    </Box>
                  ) : null;
                })}
              </Box>
            )}
            {!canPublish && (
              <Typography sx={{ fontSize: 11, color: "#9CA3AF", fontStyle: "italic" }}>
                Select platforms and write a caption to publish
              </Typography>
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "stretch" }}>
            {/* Queue — always available */}
            <Tooltip title="Automatically schedules after your last queued post" arrow>
              <span style={{ display: "flex" }}>
                <Button
                  style={{
                    borderRadius: 10, padding: "0 18px", fontSize: 12, fontWeight: 600,
                    height: 38, background: "#F3F4F6", color: "#374151",
                    display: "flex", alignItems: "center", gap: 6,
                  }}
                  onClick={handleQueueClick}
                  disabled={submitting}
                >
                  <IconClock size={14} /> Queue
                </Button>
              </span>
            </Tooltip>

            {/* Schedule — opens date/time picker */}
            <Tooltip title="Pick a specific date and time to publish" arrow>
              <span style={{ display: "flex" }}>
                <Button
                  style={{
                    borderRadius: 10, padding: "0 18px", fontSize: 12, fontWeight: 600,
                    height: 38, background: "linear-gradient(135deg, #E0E7FF, #C7D2FE)", color: "#4338CA",
                    display: "flex", alignItems: "center", gap: 6,
                  }}
                  onClick={handleScheduleClick}
                  disabled={submitting || !canPublish}
                >
                  <IconCalendar size={14} /> Schedule
                </Button>
              </span>
            </Tooltip>

            {/* Publish (admin/publisher only) OR Submit for Approval (editor/viewer) */}
            {hasPublishPermission ? (
              <Button
                gradient
                style={{
                  borderRadius: 10, padding: "0 22px", fontSize: 12, fontWeight: 700,
                  height: 38, display: "flex", alignItems: "center", gap: 6,
                }}
                onClick={() => submitPost("published")}
                disabled={submitting || !canPublish}
              >
                <IconSend size={14} /> Publish
              </Button>
            ) : (
              <Button
                style={{
                  borderRadius: 10, padding: "0 18px", fontSize: 12, fontWeight: 600,
                  height: 38, background: "linear-gradient(135deg, #EDE9FE, #F3E8FF)", color: "#7C3AED",
                  display: "flex", alignItems: "center", gap: 6,
                }}
                onClick={() => submitPost("pending_approval")}
                disabled={submitting || !canPublish}
              >
                <IconCheckbox size={14} /> Submit for Approval
              </Button>
            )}
          </Box>
        </Box>
      </Dialog>

      {/* ── Schedule Date/Time Picker Dialog ── */}
      <Dialog
        open={scheduleDialogOpen}
        onClose={() => setScheduleDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}
      >
        <Box sx={{
          background: "linear-gradient(135deg, #4338CA 0%, #6366F1 100%)",
          px: 3, py: 2.5,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{
              width: 32, height: 32, borderRadius: 2,
              bgcolor: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <IconCalendar size={18} color="#fff" />
            </Box>
            <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>Schedule Post</Typography>
          </Box>
          <IconButton onClick={() => setScheduleDialogOpen(false)} sx={{ color: "#fff" }} size="small">
            <IconX size={18} />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          <Typography sx={{ fontSize: 13, color: "#6B7280", mb: 3, lineHeight: 1.6 }}>
            Choose the exact date and time for this post to go live.
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateTimePicker
              value={scheduleDateTime}
              onChange={(val) => setScheduleDateTime(val)}
              minDateTime={dayjs()}
              slotProps={{
                textField: {
                  fullWidth: true,
                  sx: {
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2, fontSize: 14,
                      "&.Mui-focused fieldset": { borderColor: "#6366F1" },
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
          {scheduleDateTime && (
            <Paper elevation={0} sx={{
              mt: 2, p: 1.5, borderRadius: 2,
              bgcolor: "rgba(99,102,241,0.06)", border: "1px solid rgba(99,102,241,0.15)",
              textAlign: "center",
            }}>
              <Typography sx={{ fontSize: 12, color: "#4338CA", fontWeight: 600 }}>
                This post will go live on {scheduleDateTime.format("dddd, MMMM D, YYYY")} at {scheduleDateTime.format("h:mm A")}
              </Typography>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
          <Button
            style={{
              borderRadius: 10, padding: "8px 18px", fontSize: 12, fontWeight: 600,
              background: "#F3F4F6", color: "#374151",
            }}
            onClick={() => setScheduleDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button
            gradient
            style={{
              borderRadius: 10, padding: "8px 22px", fontSize: 12, fontWeight: 700,
              display: "flex", alignItems: "center", gap: 6,
            }}
            onClick={handleScheduleConfirm}
            disabled={submitting || !scheduleDateTime}
          >
            <IconCalendar size={14} /> Confirm Schedule
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar for Queue / Schedule confirmation (above modal so it stays visible when dialog closes) ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 10 }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 3, fontWeight: 600, fontSize: 13 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
